import { SizeType } from 'antd/es/config-provider/SizeContext';
import classNames from 'classnames';
import React, { FC } from 'react';
import { ProjectId, TaskId } from 'api/models';
import { FieldOption, StatusGroup } from 'api/tasks';
import { MoreActions } from 'components/atoms/delete-in-more-actions';
import { CustomDueDatePicker } from 'components/atoms/due-date-picker';
import { TemplateAssigneePicker } from 'components/molecules/assignee-picker/template-assignee-picker';
import { TaskAssigneePicker } from 'components/molecules/squad-picker/assignee-picker';
import {
  optionsToSquadPayload,
  squad,
  squadToOptions,
} from 'components/molecules/squad-picker/utils';
import { ListItem } from 'components/molecules/tasks/list-item';
import { TaskNameCell } from 'components/molecules/tasks/task-name-cell';
import { Task } from 'data/tasks';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import styles from './task-item.module.css';

interface TaskItemProps {
  assignees?;
  canAddDate?: boolean;
  disableStatusDropdown?: boolean;
  size?: SizeType;
  data: Task;
  readOnly?: boolean;
  renderExtraActions?: (data) => React.ReactNode;
  onTaskNameClick: (data) => void;
  handleDueDateChange: (
    taskId: number,
    payload: {
      startDate?: number;
      dueDate?: number;
    },
  ) => void;
  handleAssigneeChange: (taskId: number, selectedAssignee, options?) => void;
  handleStatusChange?: (
    taskId: number,
    selectedStatus: FieldOption<StatusGroup>,
  ) => void;
  onDelete: (task: Task) => void;
  attachTo?: HTMLElement | null;
}

export const TaskItem: FC<TaskItemProps> = (props) => {
  const {
    canAddDate = true,
    disableStatusDropdown,
    size,
    data,
    readOnly,
    renderExtraActions,
    onTaskNameClick,
    handleAssigneeChange,
    onDelete,
    handleDueDateChange,
    attachTo,
  } = props;

  const onHandleDueDateChange = ([selectedStartDate, selectedDueDate]) => {
    if (selectedDueDate || selectedStartDate) {
      const payload: { [key: string]: number } = {};

      if (selectedStartDate) {
        payload.startDate = selectedStartDate;
      }
      if (selectedDueDate) {
        payload.dueDate = selectedDueDate;
      }
      handleDueDateChange(data.taskId, payload);
    }
  };

  const rowClass = classNames(styles.list_item, {
    private: data.private,
  });
  const { hasAccessToResource } = useWithAccess();
  const projectResource = useProjectAsRBACResource(data.project);
  const hasWriteAccess = data.project
    ? hasAccessToResource({
        // rule: 'DOCUMENT_TASK:WRITE',
        rule: 'TASK:WRITE',
        resourceData: projectResource,
      })
    : true;
  const hasDeleteAccess = data.project
    ? hasAccessToResource({
        // rule: 'DOCUMENT_TASK:WRITE',
        rule: 'TASK:WRITE',
        resourceData: projectResource,
      })
    : true;

  return (
    <ListItem
      key={data.taskId}
      className={rowClass}
      content={
        <TaskNameCell
          attachTo={attachTo}
          disableStatusDropdown={disableStatusDropdown || !hasWriteAccess}
          onTaskNameClick={onTaskNameClick}
          taskName={data.taskName}
          progress={data.progress}
          atRisk={data.atRisk}
          taskId={TaskId(data.project, data.taskId)}
          fields={data.fields}
          size={size}
          readOnly={readOnly}
          projectId={data.project}
        />
      }
      overlay={
        Boolean(data.taskId) && (
          <div className={styles.overlay}>
            <div className={readOnly ? styles.readOnly : ''}>
              {data.project ? (
                <TaskAssigneePicker
                  isMini
                  projectId={ProjectId(data.project)}
                  onChange={(latest, resolveCallback?: (value) => void) =>
                    handleAssigneeChange(
                      data.taskId,
                      optionsToSquadPayload(latest),
                      resolveCallback,
                    )
                  }
                  isPrivate={data.private}
                  value={squadToOptions(squad(data.assignee))}
                  dueDate={data.dueDate}
                  startDate={data.startDate}
                  isDisabled={!hasWriteAccess}
                />
              ) : (
                <TemplateAssigneePicker
                  isMini
                  value={data.assignee}
                  onDone={(selected) =>
                    handleAssigneeChange(data.taskId, selected)
                  }
                />
              )}
            </div>

            {canAddDate && (
              <CustomDueDatePicker
                className={styles.due_date}
                disabled={!data.taskId || !hasWriteAccess}
                onDateChange={onHandleDueDateChange}
                size="small"
                dueDate={data.dueDate}
              />
            )}
            {renderExtraActions ? renderExtraActions(data) : null}
            {!readOnly && hasDeleteAccess && (
              <MoreActions
                data={data}
                onDelete={(task) => onDelete(task)}
                disabledAction={!data?.taskId}
              />
            )}
          </div>
        )
      }
    />
  );
};
